'use client';

/**
 * MeridianComposer
 *
 * THE Meridian chat composer. One component, one action set — used by the start
 * screen (no chat yet) and by an active conversation, so a user never loses an
 * affordance by virtue of which screen they're on: attach files, attach a
 * screenshot, connect apps, reach for a skill, and switch the chat model inline.
 *
 * Two text-binding modes, one rendered surface:
 *  - controlled   (`value` + `onValueChange`) — active chat, where the host owns
 *    the draft so suggestions/retry can write into it.
 *  - uncontrolled (`value` omitted) — the start screen, whose textarea is driven
 *    by a ref so Enter-to-send stays IME-safe without a per-keystroke re-render.
 *
 * Attachments are owned here (see `useComposerAttachments`) and handed to
 * `onSubmit` alongside the form event, then cleared — identical on both screens.
 *
 * TYPING_FOCUS_STABILITY: the `<textarea>` is rendered by THIS file, in a fixed
 * position in the tree, and its `disabled` binding is the explicit `disabled`
 * prop alone — never `isUploading`/`isBusy` or any other background flag. The
 * badges and the submit button are siblings, so nothing about them can remount
 * a focused field. Guarded by
 * `tests/components/ui/MeridianComposer.characterization.test.tsx`.
 */

import React, { memo, useCallback, useEffect, useState } from 'react';
import { useGlobalModalStack } from '@west-tennessee-consulting/ui/components/global-modal-stack';
import type { FilePart } from '../../../lib/db/types.js';
import type { AttachedSkill } from '../../skills/types.js';
import { CHAT_SKILLS_MODAL_ID } from '../../skills/constants.js';
import { ComposerPlusMenu } from './ComposerPlusMenu.js';
import { ModelPickerMenu } from './ModelPickerMenu.js';
import { useComposerAttachments } from './useComposerAttachments.js';
import { useInviteCaretKeyframes } from './useInviteCaret.js';
import { AttachedFileBadges, AttachedSkillBadge } from './ComposerBadges.js';
import { ComposerSubmitButton } from './ComposerSubmitButton.js';

const ICON_BUTTON_CLASS =
  'inline-flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50';

export interface MeridianComposerProps {
  /** Chat the uploads are filed under. The start screen mints this client-side. */
  chatId: string;
  /** Meridian API base (uploads, model preference). */
  apiBaseUrl?: string;
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  /** Controlled draft. Omit for the uncontrolled (ref-driven) mode. */
  value?: string;
  onValueChange?: (value: string) => void;
  /** Receives the form event plus the currently attached file parts. */
  onSubmit: (e: React.FormEvent<HTMLFormElement>, files: FilePart[]) => void | Promise<void>;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
  /** True while a request is in flight OR streaming — swaps Send for Stop. */
  isBusy?: boolean;
  onStop?: () => void;
  placeholder?: string;
  rows?: number;
  maxHeightPx?: number;
  /** Focus the field on mount (start screen). */
  autoFocus?: boolean;
  /** Blinking invitation caret while empty & unfocused (start screen). */
  showInviteCaret?: boolean;
  /** Inline model switcher (default true). */
  showModelPicker?: boolean;
  /** MCP connections page — enables the "Connect apps" row in the "+" menu. */
  mcpConnectionsHref?: string;
  /** Skill attached to the next message; the host must mount `ChatSkillsModal`. */
  attachedSkill?: AttachedSkill | null;
  onClearAttachedSkill?: () => void;
  /** When false, hides the "Use a skill" row (host has no skills modal mounted). */
  skillsEnabled?: boolean;
  /** When false, hides the file/screenshot rows. */
  attachmentsEnabled?: boolean;
  onFilesSelected?: (files: FilePart[]) => void;
}

function MeridianComposerComponent({
  chatId,
  apiBaseUrl = '/api/meridian',
  inputRef,
  value,
  onValueChange,
  onSubmit,
  onKeyDown,
  disabled = false,
  isBusy = false,
  onStop,
  placeholder = 'Ask Meridian…  (Enter to send, Shift+Enter for a new line)',
  rows = 2,
  maxHeightPx = 200,
  autoFocus = false,
  showInviteCaret = false,
  showModelPicker = true,
  mcpConnectionsHref,
  attachedSkill = null,
  onClearAttachedSkill,
  skillsEnabled = true,
  attachmentsEnabled = true,
  onFilesSelected,
}: MeridianComposerProps) {
  const isControlled = value !== undefined;
  const [plusOpen, setPlusOpen] = useState(false);
  const [hasUncontrolledText, setHasUncontrolledText] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const { openModal } = useGlobalModalStack();

  const {
    uploadedFiles,
    isUploading,
    uploadError,
    fileInputRef,
    screenshotInputRef,
    fileAccept,
    imageAccept,
    openFilePicker,
    openScreenshotPicker,
    handleFileInputChange,
    removeFile,
    clearFiles,
  } = useComposerAttachments({ chatId, apiBaseUrl, onFilesSelected });

  useInviteCaretKeyframes(showInviteCaret);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus, inputRef]);

  // Reset the auto-grown height when a controlled draft is cleared (after send).
  useEffect(() => {
    if (isControlled && !value && inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
  }, [isControlled, value, inputRef]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const el = e.target;
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, maxHeightPx)}px`;
      if (isControlled) {
        onValueChange?.(el.value);
      } else {
        setHasUncontrolledText(el.value.trim().length > 0);
      }
    },
    [isControlled, maxHeightPx, onValueChange]
  );

  const handleFormSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      await onSubmit(e, uploadedFiles);
      clearFiles();
      if (!isControlled) setHasUncontrolledText(false);
    },
    [clearFiles, isControlled, onSubmit, uploadedFiles]
  );

  const handleUseSkill = useCallback(() => openModal(CHAT_SKILLS_MODAL_ID), [openModal]);

  const hasText = isControlled ? value.trim().length > 0 : hasUncontrolledText;
  const canSend = !disabled && (hasText || uploadedFiles.length > 0);
  // The invitation caret only shows before the field is focused; once focused,
  // the browser's own caret takes over so we never render two.
  const inviteCaretVisible = showInviteCaret && !hasText && !isFocused;

  return (
    <form onSubmit={handleFormSubmit} className="w-full">
      {/* Hidden pickers */}
      {attachmentsEnabled && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileInputChange}
            disabled={isUploading || disabled}
            accept={fileAccept}
            className="hidden"
            aria-label="Upload file"
          />
          <input
            ref={screenshotInputRef}
            type="file"
            onChange={handleFileInputChange}
            disabled={isUploading || disabled}
            accept={imageAccept}
            className="hidden"
            aria-label="Upload screenshot"
          />
        </>
      )}

      <div className="rounded-2xl border border-border bg-card shadow-sm transition-colors focus-within:border-primary/50 focus-within:shadow-md">
        <div className="relative px-4 pt-3">
          {inviteCaretVisible && (
            <div
              aria-hidden
              className="pointer-events-none absolute left-4 top-3 flex items-center text-base text-muted-foreground"
            >
              <span
                className="mr-0.5 inline-block h-[1.15em] w-[2px] bg-primary"
                style={{ animation: 'meridianCaretBlink 1.1s step-end infinite' }}
              />
              <span>{placeholder}</span>
            </div>
          )}
          <textarea
            ref={inputRef}
            {...(isControlled ? { value } : {})}
            onChange={handleChange}
            onKeyDown={onKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            // Native placeholder is suppressed while the invite caret shows so
            // the text never renders twice.
            placeholder={inviteCaretVisible ? '' : placeholder}
            aria-label="Ask Meridian"
            // The ONLY disabled binding. Never add a background-load flag here.
            disabled={disabled}
            rows={rows}
            style={{ maxHeight: `${maxHeightPx}px` }}
            className="min-h-[3rem] w-full resize-none overflow-y-auto break-words bg-transparent text-foreground placeholder-muted-foreground focus:outline-none disabled:opacity-60"
          />
        </div>

        <div className="flex items-center justify-between gap-2 px-3 pb-3 pt-1">
          <div className="flex min-w-0 items-center gap-1">
            <ComposerPlusMenu
              open={plusOpen}
              onOpenChange={setPlusOpen}
              disabled={disabled}
              onAddFiles={attachmentsEnabled ? openFilePicker : undefined}
              onAddScreenshots={attachmentsEnabled ? openScreenshotPicker : undefined}
              onUseSkill={skillsEnabled ? handleUseSkill : undefined}
              mcpConnectionsHref={mcpConnectionsHref}
              side="top"
              align="start"
              triggerClassName={ICON_BUTTON_CLASS}
            />
            {showModelPicker && <ModelPickerMenu disabled={disabled} apiBaseUrl={apiBaseUrl} />}
          </div>

          <ComposerSubmitButton isBusy={isBusy} canSend={canSend} onStop={onStop} />
        </div>
      </div>

      {attachedSkill && (
        <AttachedSkillBadge skill={attachedSkill} onClear={onClearAttachedSkill} />
      )}

      {isUploading && <div className="px-4 pt-2 text-sm text-muted-foreground">Attaching…</div>}

      {uploadError && (
        <div role="alert" className="px-4 pt-2 text-sm text-destructive">
          {uploadError}
        </div>
      )}

      {uploadedFiles.length > 0 && (
        <AttachedFileBadges files={uploadedFiles} onRemove={removeFile} />
      )}
    </form>
  );
}

export const MeridianComposer = memo(MeridianComposerComponent);
